import * as React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { DuplicateOptions, DuplicateGroup, AnalysedGroup } from './types';
import OptionsStep from './OptionsStep';
import FetchStep from './FetchStep';
import AnalyseStep from './AnalyseStep';
import ExecutionStep from './ExecutionStep';

type ModalStep = 'options' | 'fetch' | 'analyse' | 'execute';

interface StepDef {
    id: ModalStep;
    labelKey: TK;
}

const STEPS: StepDef[] = [
    { id: 'options', labelKey: TK.clearDuplicatesStepOptions },
    { id: 'fetch',   labelKey: TK.clearDuplicatesStepFetch   },
    { id: 'analyse', labelKey: TK.clearDuplicatesStepAnalyse },
    { id: 'execute', labelKey: TK.clearDuplicatesStepExecution },
];

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const ClearDuplicatesModal: React.FC<Props> = ({ isOpen, onClose }) => {
    const t = useTranslations();
    const [step, setStep] = React.useState<ModalStep>('options');
    const [options, setOptions] = React.useState<DuplicateOptions>({ field: 'email', deletionRule: 'noEntities' });
    const [groups, setGroups] = React.useState<DuplicateGroup[] | null>(null);
    const [analysed, setAnalysed] = React.useState<AnalysedGroup[] | null>(null);

    React.useEffect(() => {
        if (!isOpen) {
            setStep('options');
            setOptions({ field: 'email', deletionRule: 'noEntities' });
            setGroups(null);
            setAnalysed(null);
        }
    }, [isOpen]);

    const currentStepIndex = STEPS.findIndex(s => s.id === step);

    const goToStep = React.useCallback((target: ModalStep) => {
        if (target === 'options') {
            setGroups(null);
            setAnalysed(null);
        } else if (target === 'fetch') {
            setAnalysed(null);
        }
        setStep(target);
    }, []);

    const handleFetchComplete = React.useCallback((result: DuplicateGroup[]) => {
        setGroups(result);
        setStep('analyse');
    }, []);

    const handleAnalyseComplete = React.useCallback((result: AnalysedGroup[]) => {
        setAnalysed(result);
        setStep('execute');
    }, []);

    const stepIndicator = (
        <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacer-M)',
            paddingBottom: 'var(--spacer-M)',
            borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
        }}>
            {STEPS.map((s, i) => {
                const isPast = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const circleColor = isCurrent ? 'var(--appBlue)' : isPast ? 'var(--appGreen)' : 'var(--appLigthGrey)';
                const labelColor = isCurrent ? 'var(--appBlue)' : isPast ? 'var(--appGreen)' : 'var(--text-muted)';

                return (
                    <React.Fragment key={s.id}>
                        {i > 0 && (
                            <div style={{
                                flex: 1,
                                height: '2px',
                                marginTop: '13px',
                                background: isPast ? 'var(--appGreen)' : 'var(--appLigthGrey)',
                                transition: 'background 0.2s',
                            }} />
                        )}
                        <div
                            onClick={isPast && step !== 'execute' ? () => goToStep(s.id) : undefined}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'var(--spacer-XXS)',
                                cursor: isPast && step !== 'execute' ? 'pointer' : 'default',
                                minWidth: '64px',
                            }}
                        >
                            <div style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                background: circleColor,
                                color: isCurrent || isPast ? 'white' : 'var(--text-muted)',
                                transition: 'background 0.2s',
                                flexShrink: 0,
                            }}>
                                {i + 1}
                            </div>
                            <span style={{
                                fontSize: '0.72rem',
                                color: labelColor,
                                fontWeight: isCurrent ? 600 : 400,
                                whiteSpace: 'nowrap',
                                transition: 'color 0.2s',
                                textAlign: 'center',
                            }}>
                                {t(s.labelKey)}
                            </span>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );

    return (
        <Modal isOpen={isOpen} toggle={onClose} size='xl'>
            <ModalHeader toggle={onClose}>{t(TK.clearDuplicates)}</ModalHeader>
            <ModalBody>
                {stepIndicator}
                {step === 'options' && (
                    <OptionsStep options={options} onOptionsChange={setOptions} />
                )}
                {step === 'fetch' && (
                    <FetchStep options={options} onComplete={handleFetchComplete} />
                )}
                {step === 'analyse' && groups && (
                    <AnalyseStep groups={groups} options={options} onComplete={handleAnalyseComplete} />
                )}
                {step === 'execute' && analysed && (
                    <ExecutionStep groups={analysed} onClose={onClose} />
                )}
            </ModalBody>
            {step === 'options' && (
                <ModalFooter>
                    <Button color='success' onClick={() => setStep('fetch')}>
                        {t(TK.next)}
                    </Button>
                </ModalFooter>
            )}
            {step === 'fetch' && groups && (
                <ModalFooter>
                    <Button color='secondary' onClick={() => goToStep('options')}>
                        {t(TK.back)}
                    </Button>
                </ModalFooter>
            )}
            {step === 'analyse' && (
                <ModalFooter>
                    <Button color='secondary' onClick={() => goToStep('fetch')}>
                        {t(TK.back)}
                    </Button>
                </ModalFooter>
            )}
        </Modal>
    );
};

export default ClearDuplicatesModal;
